import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MenuItem, MenuItems, TSidebarActions } from "../types";
import { getOnlyChildObjects } from "../helpers/filterItemsBySearch";
import { debounce } from "throttle-debounce";
import { getSortResult } from "../helpers/getSortResult";
import { TEntityInfo } from "../../types/utility";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { setSelectedObjectId } from "../../../app/state/storageObjectSlice/slice";
import { StorageScope } from "../../../services/api/instances/storageApi/types/service_types";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectServiceUi } from "../../../app/state/uiSlice/selectors";
import { setServiceUi } from "../../../app/state/uiSlice/slice";

export const useSidebarHeader = (
  menuItems: MenuItems,
  onSelect: (id: string) => void,
  service: StorageScope,
) => {
  const dispatch = useAppDispatch();
  const ui = useAppSelector((state) => selectServiceUi(state, service));
  const [isOpenSearchPanel, setIsOpenSearchPanel] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [items, setItems] = useState<MenuItems | null>(null);

  const onSelectRef = useRef(onSelect);

  const resultItems = searchQuery === "" ? menuItems : (items ?? []);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const childItems = useMemo(() => getOnlyChildObjects(menuItems), [menuItems]);

  const debouncedSetSearchingMenuItems = useRef(
    debounce(1000, (results: MenuItems) => {
      setItems(results);
      if (results.length > 0) onSelectRef.current(results[0].id);
    }),
  ).current;

  useEffect(() => {
    return () => {
      debouncedSetSearchingMenuItems.cancel();
    };
  }, [debouncedSetSearchingMenuItems]);

  const handleSearchItem = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const query = event.target.value;
      setSearchQuery(query);

      debouncedSetSearchingMenuItems.cancel({ upcomingOnly: true });

      if (query === "") {
        setItems(null);
        return;
      }
      const scored = childItems
        .map((item) => ({ item, score: getSortResult(item, query) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ item }) => item);
      debouncedSetSearchingMenuItems(scored.slice(0, 10));
    },
    [childItems, debouncedSetSearchingMenuItems],
  );

  const handleSelectSidebarItem = useCallback((item: MenuItem | null) => {
    dispatch(setSelectedObjectId({ id: item?.id ?? "", service }));
    setSelectedItem(item);
  }, []);

  const handleSidebarHeaderActions = (
    type: TSidebarActions,
    info: TEntityInfo | null,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    switch (type) {
      case "search": {
        setIsOpenSearchPanel((prev) => {
          if (prev) {
            setSearchQuery("");
            setItems(menuItems);
            debouncedSetSearchingMenuItems.cancel();
            return false;
          } else {
            return true;
          }
        });
        return;
      }
      case "search_object_selecting": {
        event && handleSearchItem(event);
        return;
      }
      case "clear": {
        setSearchQuery("");
        setItems(menuItems);
        debouncedSetSearchingMenuItems.cancel();
        return;
      }
      case "sidebar_layout_control": {
        if (ui?.sidebar) {
          dispatch(
            setServiceUi({
              service,
              ui: {
                sidebar: {
                  ...ui?.sidebar,
                  isOpen: !ui.sidebar.isOpen,
                },
              },
            }),
          );
        }
        return;
      }
      default:
        return;
    }
  };

  return {
    ui,
    resultItems,
    selectedItem,
    searchQuery,
    isOpenSearchPanel,
    handleSearchItem,
    handleSelectSidebarItem,
    handleSidebarHeaderActions,
  };
};
